import React, { useState, useRef, useContext, useEffect } from 'react'
import * as Sentry from '@sentry/browser'
import styled from 'styled-components'
import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock'

import colors from 'styles/colors'
import spacing from 'styles/spacing'

import {
  Icon,
  Form,
  Switch,
  Container,
  BtnBack,
  Button,
  AlertContent,
  Fonts,
  Modal,
  OverlayPage,
  Label,
  LabelBox,
} from 'components/base-ui'
import useForm from 'hooks/useForm'
import {
  CardGroup,
  Card,
  CardEmpty,
  CardText,
  IconPaymentWrapper,
  ScrollWrapper,
  BtnBackWrapper,
  Title,
  ListDetailCards,
  WrapperInfoBox,
} from './styles'

import { getErrorMessage } from 'helpers/validators'
import useOmise from 'services/hooks/useOmise'
import ListCardCreditCard from './ListCardCreditCard'
import FormCreditCard from './FormCreditCard'

import { AppContext } from 'contexts/AppProvider'
import { isDevelopment } from 'variables/environment'
import { CustomerPaymentTypeEnum } from 'enums'
import PageOverlayEditCreditCard from './PageOverlayEditCreditCard'
import useSubPage from 'hooks/useSubPage'

import { useListCreditCardsQuery } from 'graphQL/hatostore/generate/operations'
import { useTranslation } from 'react-i18next'

import { getSrcSetStr } from 'helpers/image'
import { cloneObject } from 'helpers'

const initialFormData = {
  cardNumber: '',
  cardHolderName: '',
  expireDate: '',
  cvc: '',
}

const PaymentMethod = ({
  paymentMethod,
  onSelectPayment,
  show,
  vendorSettingPaymentMethods,
  onBack,
  vendorID,
  onUpdatePaymentMethod,
  onAfterRemoveCreditCardCompleted,
  isPickUp = false,
}) => {
  const { setCustomerCreditCard, customerCreditCard, brand, accessToken } = useContext(AppContext)
  const isEnableFeatureCreditCardManagement = true
  const { subPageName, goToSubPage } = useSubPage()

  const refWrapper = useRef(null)
  const refForm = useRef(null)
  const refCardNumber = useRef(null)
  const refCardHolderName = useRef(null)
  const refExpireDate = useRef(null)
  const refCVCInput = useRef(null)
  const [stateView, setStateView] = useState('')
  const [isRequestPayment, setIsRequestPayment] = useState(false)
  const [creditCardBrand, setCreditCardBrand] = useState('')
  const [isWantToRememberCreditCard, setIsWantToRememberCreditCard] = useState(true)
  const [isUseRemeberCreditCard, setIsUseRemeberCreditCard] = useState(false)
  const [isSavedCreditCard, setIsSavedCreditCard] = useState(false)
  const [errorMsgCreditCard, setErrorMsgCreditCard] = useState('')
  const [selectedCreditCard, setSelectedCreditCard] = useState(null)
  const [currentEditCreditCard, setCurrentEditCreditCard] = useState(null)
  const [isShowMessageAddNewCreditCard, setIsShowMessageAddNewCreditCard] = useState(false)
  const [isShowMessageRemoveCreditCard, setIsShowMessageRemoveCreditCard] = useState(false)
  const [
    isOnFocusInput,
    // setIsOnFocusInput
  ] = useState(false)

  const { t } = useTranslation()

  const [isOpenModalConfirmUnremember, setIsOpenModalConfirmUnremember] = useState(false)
  const [isShowWarningText, setIsShowWarningText] = useState(false)
  const [warningText, setWarningText] = useState('')

  // KPlus
  const [kplusPhoneNumber, setKplusPhoneNumber] = useState('')
  const [kplusPhoneNumberErrorMsg, setKplusPhoneNumberErrorMsg] = useState('')
  const [isKplusPhoneNumberSubmitted, setIsKplusPhoneNumberSubmitted] = useState(false)

  // KPromptpay
  const [kPromptpayPhoneNumber, setKPromptpayPhoneNumber] = useState('')
  const [kPromptpayPhoneNumberErrorMsg, setKPromptpayPhoneNumberErrorMsg] = useState('')
  const [isKPromptpayPhoneNumberSubmitted, setIsKPromptpayPhoneNumberSubmitted] = useState(false)

  const resListCreditCards = useListCreditCardsQuery({
    variables: { vendorID },
    skip: !vendorID || !accessToken,
    context: {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    },
  })

  const listCreditCards = resListCreditCards?.data?.listCreditCards ? resListCreditCards?.data?.listCreditCards : []

  // SetView
  useEffect(() => {
    if (vendorSettingPaymentMethods?.length > 0) {
      if (paymentMethod.type === '') {
        setStateView(vendorSettingPaymentMethods[0])
      } else {
        setStateView(paymentMethod.type)
      }
    }
  }, [paymentMethod.type, vendorSettingPaymentMethods])

  // GET OMISE user credit card
  // ---------------------------------------------------------
  const { data: formCreditData, handleOnChange, setAsyncInitData } = useForm(initialFormData)

  let _initialFormData = cloneObject(initialFormData)
  let haveOmiseCreditCard = false
  let lastDigitRememberCard = ''
  if (paymentMethod.creditCard?.cardLastDigits) {
    haveOmiseCreditCard = true
    lastDigitRememberCard = paymentMethod.creditCard.cardLastDigits

    // InitialFormData from rememberCard
    _initialFormData = {
      cardNumber: `**** **** **** ${lastDigitRememberCard}`,
      cardHolderName: '************',
      expireDate: '****',
      cvc: '***',
    }
  }

  useEffect(() => {
    if (haveOmiseCreditCard && show) {
      setAsyncInitData(_initialFormData)
      setCreditCardBrand(paymentMethod.creditCard?.cardBrand ?? '')
      setSelectedCreditCard(paymentMethod.creditCard)
      setIsSavedCreditCard(true)

      if (customerCreditCard === null) {
        setIsUseRemeberCreditCard(true)
        setIsWantToRememberCreditCard(true)
      } else {
        setIsWantToRememberCreditCard(paymentMethod.rememberCard)
      }
    } else if (!haveOmiseCreditCard) {
      setSelectedCreditCard(null)
    }

    if (show) {
      setIsShowMessageAddNewCreditCard(false)
      setIsShowMessageRemoveCreditCard(false)
      // Reset KPlus and KPromptPay state
      setIsKplusPhoneNumberSubmitted(false)
      setKplusPhoneNumberErrorMsg('')
      if (paymentMethod.type === CustomerPaymentTypeEnum.KPaymentKPlusNumber) {
        setKplusPhoneNumber(paymentMethod.phoneNumber)
      } else {
        setKplusPhoneNumber('')
      }

      setIsKPromptpayPhoneNumberSubmitted(false)
      setKPromptpayPhoneNumberErrorMsg('')
      if (paymentMethod.type === CustomerPaymentTypeEnum.KPaymentPromptPayNumber) {
        setKPromptpayPhoneNumber(paymentMethod.phoneNumber)
      } else {
        setKPromptpayPhoneNumber('')
      }
    }
  }, [paymentMethod, show])
  // ---------------------------------------------------------

  const omise = useOmise({
    omisePublicKey: brand?.omisePublicKey ? brand.omisePublicKey : process.env.REACT_APP_OMISE_PUBLIC_KEY,
  })
  const omiseCreateToken = omise.createToken

  useEffect(() => {
    if (refWrapper) {
      if (show) {
        disableBodyScroll(refWrapper.current)
      } else {
        enableBodyScroll(refWrapper.current)
      }
    }
  }, [show])

  useEffect(() => {
    if (isShowWarningText) {
      setTimeout(() => {
        setIsShowWarningText(false)
        setTimeout(() => {
          setWarningText('')
        }, 300)
      }, 2000)
    }
  }, [isShowWarningText])

  const isShowMsgNoticePaymentAfterOrderSubmitted =
    stateView === CustomerPaymentTypeEnum.CashOnDelivery ||
    stateView === CustomerPaymentTypeEnum.SCBPromptPay ||
    stateView === CustomerPaymentTypeEnum.OmisePromptPay ||
    stateView === CustomerPaymentTypeEnum.KBankThaiQR

  return (
    <>
      <ScrollWrapper ref={refWrapper}>
        <PreloadImage src={`${process.env.PUBLIC_URL}/assets/payment/blue-card.png`} />
        <Modal isOpen={isOpenModalConfirmUnremember} onRequestClose={() => setIsOpenModalConfirmUnremember(false)}>
          <ModalContainer>
            <img alt="blue-credit-card" src={`${process.env.PUBLIC_URL}/assets/payment/blue-card.png`} />
            <ModalTitle style={{ maxWidth: '200px' }}>{t('modalConfirmUnrememberTitle')}</ModalTitle>
            <ModalText style={{ maxWidth: '220px' }}>{t('modalConfirmUnrememberDes')}</ModalText>
            <ButtonCurrentLocationWrapper>
              <Button
                type="submit"
                md2
                minWidth="214px"
                onClick={() => {
                  resetFormState()
                  const newValueFormData = cloneObject(initialFormData)
                  setAsyncInitData(newValueFormData)
                  setIsUseRemeberCreditCard(false)
                  setIsSavedCreditCard(false)
                  setIsWantToRememberCreditCard(false)
                  setIsOpenModalConfirmUnremember(false)
                }}
              >
                {t('btnConfirm')}
              </Button>
            </ButtonCurrentLocationWrapper>
            <Button type="submit" md2 minWidth="214px" outline onClick={() => setIsOpenModalConfirmUnremember(false)}>
              {t('btnBackToPage')}
            </Button>
          </ModalContainer>
        </Modal>

        <LabelAddressWarningWrapper show={isShowWarningText}>
          <Container>
            <Label type="warning" text={warningText} />
          </Container>
        </LabelAddressWarningWrapper>

        <Container style={{ paddingTop: '88px', position: 'relative' }}>
          <BtnBackWrapper>
            <Container>
              <BtnBack sm onClick={onBack} enableFastClick={false} />
            </Container>
          </BtnBackWrapper>
          <Title>{t('paymentFormTitle')}</Title>

          {isShowMessageAddNewCreditCard && (
            <LabelBox
              type="success"
              text={
                <Fonts.SmallTitleBold>
                  {t('paymentMethodPage.toast.addedNewCreditCardSuccessfully')}
                </Fonts.SmallTitleBold>
              }
              style={{
                width: '100%',
                padding: '10px',
                marginBottom: '20px',
              }}
            />
          )}

          {isShowMessageRemoveCreditCard && (
            <LabelBox
              type="success"
              text={
                <Fonts.SmallTitleBold>{t('paymentMethodPage.toast.removeCreditCardSuccessfully')}</Fonts.SmallTitleBold>
              }
              style={{
                width: '100%',
                padding: '10px',
                marginBottom: '20px',
              }}
            />
          )}

          <div>
            {vendorSettingPaymentMethods && vendorSettingPaymentMethods.length > 0 && (
              <CardGroup data-testid="list-payment-method">
                {vendorSettingPaymentMethods.includes(CustomerPaymentTypeEnum.KBankThaiQR) && (
                  <Card
                    className={stateView === CustomerPaymentTypeEnum.KBankThaiQR ? '-active' : ''}
                    onClick={() => setStateView(CustomerPaymentTypeEnum.KBankThaiQR)}
                  >
                    <IconPaymentWrapper>
                      <Icon name="qrcode" size="14px" />
                    </IconPaymentWrapper>
                    {t('paymentMethodPage.paymentMethod.qrKBank')}
                  </Card>
                )}

                {vendorSettingPaymentMethods.includes(CustomerPaymentTypeEnum.OmiseCreditCard) &&
                  !isEnableFeatureCreditCardManagement && (
                    <Card
                      id="payment-type-credit"
                      className={stateView === CustomerPaymentTypeEnum.OmiseCreditCard ? '-active' : ''}
                      onClick={() => setStateView(CustomerPaymentTypeEnum.OmiseCreditCard)}
                    >
                      <IconPaymentWrapper>
                        <Icon name="credit-card" size="14px" />
                      </IconPaymentWrapper>
                      {t('creditCard')}
                    </Card>
                  )}

                {vendorSettingPaymentMethods.includes(CustomerPaymentTypeEnum.OmiseCreditCard) &&
                  isEnableFeatureCreditCardManagement && (
                    <ListCardCreditCard
                      isActivePaymentMethod={stateView === CustomerPaymentTypeEnum.OmiseCreditCard}
                      selectedCreditCard={selectedCreditCard}
                      listCreditCards={listCreditCards}
                      onClickAddNewCreditCard={() => goToSubPage('edit-credit-card')}
                      onClickSelectCreditCard={(creditCard) => {
                        setStateView(CustomerPaymentTypeEnum.OmiseCreditCard)
                        setSelectedCreditCard(creditCard)
                      }}
                      onClickEditCreditCard={(creditCard) => {
                        setCurrentEditCreditCard(creditCard)
                        goToSubPage('edit-credit-card')
                      }}
                    />
                  )}

                {vendorSettingPaymentMethods.includes(CustomerPaymentTypeEnum.KPaymentKPlusNumber) && (
                  <Card
                    className={stateView === CustomerPaymentTypeEnum.KPaymentKPlusNumber ? '-active' : ''}
                    onClick={() => setStateView(CustomerPaymentTypeEnum.KPaymentKPlusNumber)}
                  >
                    <IconPaymentWrapper>
                      <img
                        alt="icon-kplus"
                        src={`${process.env.PUBLIC_URL}/assets/payment/icon-kplus.png`}
                        srcSet={getSrcSetStr({
                          img: `${process.env.PUBLIC_URL}/assets/payment/icon-kplus.png`,
                          hires2x: true,
                          hires3x: true,
                        })}
                      />
                    </IconPaymentWrapper>
                    {t('kplusPayment')}
                  </Card>
                )}

                {vendorSettingPaymentMethods.includes(CustomerPaymentTypeEnum.OmiseTrueMoneyWallet) && (
                  <Card
                    contentInside
                    id="trueWalletRdo"
                    data-testid="trueWalletRdo"
                    className={stateView === CustomerPaymentTypeEnum.OmiseTrueMoneyWallet ? '-active' : ''}
                    onClick={() => setStateView(CustomerPaymentTypeEnum.OmiseTrueMoneyWallet)}
                  >
                    <div style={{ display: 'flex' }}>
                      <IconPaymentWrapper>
                        <img
                          alt="icon-true-money-wallet"
                          style={{ height: '32px' }}
                          src={`${process.env.PUBLIC_URL}/assets/payment/icon-true-money-wallet.png`}
                          srcSet={getSrcSetStr({
                            img: `${process.env.PUBLIC_URL}/assets/payment/icon-true-money-wallet.png`,
                            hires2x: true,
                            hires3x: true,
                          })}
                        />
                      </IconPaymentWrapper>
                      {t('trueMoneyWallet')}
                    </div>
                    {stateView === CustomerPaymentTypeEnum.OmiseTrueMoneyWallet && (
                      <WrapperInfoBox>
                        <LabelBox
                          type="infoDark"
                          title={t('paymentConditionTitle')}
                          text={
                            <ListDetailCards>
                              <li>{t('paymentConditionRequiredContent_2')}</li>
                              <li>{t('paymentConditionRequiredContent_1')}</li>
                            </ListDetailCards>
                          }
                        />
                      </WrapperInfoBox>
                    )}
                  </Card>
                )}

                {vendorSettingPaymentMethods.includes(CustomerPaymentTypeEnum.BankTransfer) && (
                  <Card
                    className={stateView === CustomerPaymentTypeEnum.BankTransfer ? '-active' : ''}
                    onClick={() => setStateView(CustomerPaymentTypeEnum.BankTransfer)}
                  >
                    {t('bankTransfer')}
                  </Card>
                )}

                {vendorSettingPaymentMethods.includes(CustomerPaymentTypeEnum.SCBPromptPay) && (
                  <Card
                    className={stateView === CustomerPaymentTypeEnum.SCBPromptPay ? '-active' : ''}
                    onClick={() => setStateView(CustomerPaymentTypeEnum.SCBPromptPay)}
                  >
                    <IconPaymentWrapper>
                      <Icon name="qrcode" size="14px" />
                    </IconPaymentWrapper>
                    {t('qrcodePayment')}
                  </Card>
                )}

                {vendorSettingPaymentMethods.includes(CustomerPaymentTypeEnum.OmisePromptPay) && (
                  <Card
                    className={stateView === CustomerPaymentTypeEnum.OmisePromptPay ? '-active' : ''}
                    onClick={() => setStateView(CustomerPaymentTypeEnum.OmisePromptPay)}
                  >
                    <IconPaymentWrapper>
                      <Icon name="qrcode" size="14px" />
                    </IconPaymentWrapper>
                    {t('qrcodePayment')}
                  </Card>
                )}

                {vendorSettingPaymentMethods.includes(CustomerPaymentTypeEnum.KPaymentPromptPayNumber) && (
                  <Card
                    className={stateView === CustomerPaymentTypeEnum.KPaymentPromptPayNumber ? '-active' : ''}
                    onClick={() => setStateView(CustomerPaymentTypeEnum.KPaymentPromptPayNumber)}
                  >
                    <IconPaymentWrapper>
                      <Icon name="mobile" size="18px" color="#1A67C1" />
                    </IconPaymentWrapper>
                    {t('kbankPromptpayPayment')}
                  </Card>
                )}

                {vendorSettingPaymentMethods.includes(CustomerPaymentTypeEnum.CashOnDelivery) && (
                  <Card
                    className={stateView === CustomerPaymentTypeEnum.CashOnDelivery ? '-active' : ''}
                    onClick={() => setStateView(CustomerPaymentTypeEnum.CashOnDelivery)}
                  >
                    <IconPaymentWrapper>
                      <Icon name="cash" size="14px" />
                    </IconPaymentWrapper>
                    {isPickUp ? t('payAtStore') : t('cashOnDelivery')}
                  </Card>
                )}

                {brand?.features?.dineIn?.contactStaffToMakePayment && (
                  <CardEmpty>
                    <IconPaymentWrapper>
                      <Icon name="person-pin" size="18px" />
                    </IconPaymentWrapper>
                    <CardText>{t('paymentMethodPage.contactStaff')}</CardText>
                  </CardEmpty>
                )}
              </CardGroup>
            )}

            {stateView === CustomerPaymentTypeEnum.BankTransfer && (
              <PaymentMethodWrapper>
                <Form.InputNotice color={colors.gray900}>
                  <Icon name="notice-circle-2" size="13px" color="#2E8CE2" />
                  {t('noticePaymentAfterOrderSubmitted')}
                </Form.InputNotice>
              </PaymentMethodWrapper>
            )}

            {isShowMsgNoticePaymentAfterOrderSubmitted && (
              <PaymentMethodWrapper>
                <AlertContent type="info" text={t('noticePaymentAfterOrderSubmitted')} />
              </PaymentMethodWrapper>
            )}

            {stateView === CustomerPaymentTypeEnum.KPaymentPromptPayNumber && (
              <PaymentMethodWrapper>
                <FormPhoneNumber>
                  <Container>
                    <img
                      alt="icon-promptpay"
                      style={{ margin: '10px 0 5px -2px' }}
                      src={`${process.env.PUBLIC_URL}/assets/payment/icon-promptpay.png`}
                      srcSet={getSrcSetStr({
                        img: `${process.env.PUBLIC_URL}/assets/payment/icon-promptpay.png`,
                        hires2x: true,
                        hires3x: true,
                      })}
                    />
                    <Fonts.BaseTitleBold style={{ marginBottom: `${spacing.md - 2}px` }}>
                      {t('kbankPromptpayPaymentTitle')}
                    </Fonts.BaseTitleBold>
                    <Form.InputManual
                      type="text"
                      placeholderFloat={t('tel')}
                      placeholder="089-123-4567"
                      value={kPromptpayPhoneNumber}
                      showBothPlaceholder
                      onChange={(e) => {
                        if (isKPromptpayPhoneNumberSubmitted) {
                          const errorMsg = validationPhoneNumber(e.target.value)
                          setKPromptpayPhoneNumberErrorMsg(errorMsg)
                        }
                        setKPromptpayPhoneNumber(e.target.value)
                      }}
                      required
                      error={kPromptpayPhoneNumberErrorMsg}
                    />
                  </Container>
                </FormPhoneNumber>
              </PaymentMethodWrapper>
            )}

            {stateView === CustomerPaymentTypeEnum.KPaymentKPlusNumber && (
              <PaymentMethodWrapper>
                <Form.InputNotice color={colors.gray900}>
                  <Icon name="notice-circle-2" size="13px" color="#2E8CE2" />
                  {t('kplusPaymentNotice')}
                </Form.InputNotice>
                <FormPhoneNumber>
                  <Container>
                    <Fonts.BaseTitleBold style={{ marginBottom: `${spacing.md - 2}px` }}>
                      {t('kplusPaymentTitle')}
                    </Fonts.BaseTitleBold>
                    <Form.InputManual
                      type="text"
                      placeholderFloat={t('tel')}
                      placeholder="089-123-4567"
                      value={kplusPhoneNumber}
                      showBothPlaceholder
                      onChange={(e) => {
                        if (isKplusPhoneNumberSubmitted) {
                          const errorMsg = validationPhoneNumber(e.target.value)
                          setKplusPhoneNumberErrorMsg(errorMsg)
                        }
                        setKplusPhoneNumber(e.target.value)
                      }}
                      required
                      error={kplusPhoneNumberErrorMsg}
                    />
                  </Container>
                </FormPhoneNumber>
              </PaymentMethodWrapper>
            )}
          </div>

          {/* State View credit */}
          <CreditsCardViewBox
            show={stateView === CustomerPaymentTypeEnum.OmiseCreditCard && !isEnableFeatureCreditCardManagement}
          >
            <FormCreditCard
              formID="form-credit-card"
              formCreditData={formCreditData}
              setAsyncInitData={setAsyncInitData}
              handleOnChange={handleOnChange}
              haveOmiseCreditCard={haveOmiseCreditCard}
              isSavedCreditCard={isSavedCreditCard}
              onUpdatedForm={() => {
                setErrorMsgCreditCard('')
              }}
              onResetForm={() => {
                setIsUseRemeberCreditCard(false)
                setIsSavedCreditCard(false)
              }}
              refForm={refForm}
              refCardNumber={refCardNumber}
              refCardHolderName={refCardHolderName}
              refExpireDate={refExpireDate}
              refCVCInput={refCVCInput}
              errorMsgCreditCard={errorMsgCreditCard}
              creditCardBrand={creditCardBrand}
              onDetectBrandCreditCard={(brand) => setCreditCardBrand(brand)}
            />

            <SwitchWrapper>
              <Switch asyncDefaultValue={isWantToRememberCreditCard} onChange={handleRememberCard}>
                {t('cardRemember')}
              </Switch>
            </SwitchWrapper>
          </CreditsCardViewBox>
        </Container>

        {!isOnFocusInput && (
          <SummaryPriceBox>
            <Container>
              <Container>
                <ButtonWrapper>
                  <Button
                    id="saveBtn"
                    data-testid="saveBtn"
                    md2
                    float
                    minWidth="none"
                    outline
                    onClick={handleOnSubmit}
                    isProcessing={isRequestPayment}
                    disabled={
                      isRequestPayment ||
                      (stateView === CustomerPaymentTypeEnum.OmiseCreditCard &&
                        isUseRemeberCreditCard &&
                        !isWantToRememberCreditCard)
                    }
                  >
                    {t('btnSavePayment')}
                  </Button>
                </ButtonWrapper>
              </Container>
            </Container>
          </SummaryPriceBox>
        )}
      </ScrollWrapper>

      <OverlayPage show={subPageName === 'edit-credit-card'}>
        <PageOverlayEditCreditCard
          creditCard={currentEditCreditCard}
          vendorID={vendorID}
          onBack={() => {
            window.history.back()
            setTimeout(() => {
              setCurrentEditCreditCard(null)
            }, 400)
          }}
          onAddedNewCreditCardCompleted={(creditCard) => {
            setIsShowMessageRemoveCreditCard(false)
            setIsShowMessageAddNewCreditCard(true)
            setSelectedCreditCard(creditCard)
            setStateView(CustomerPaymentTypeEnum.OmiseCreditCard)
            resListCreditCards && resListCreditCards.refetch()
          }}
          onRemoveCreditCardCompleted={async (creditCard) => {
            setIsShowMessageAddNewCreditCard(false)
            setIsShowMessageRemoveCreditCard(true)
            if (resListCreditCards) {
              const result = await resListCreditCards.refetch()
              if (result?.data?.listCreditCards.length > 0) {
                if (creditCard.cardID === selectedCreditCard?.cardID) {
                  const newSelectCard = result.data.listCreditCards[0]
                  setSelectedCreditCard(newSelectCard)
                  await onUpdatePaymentMethod({
                    cardID: newSelectCard.cardID,
                    type: stateView,
                  })
                }
              } else {
                setSelectedCreditCard(null)
              }
            }
            onAfterRemoveCreditCardCompleted()
          }}
        />
      </OverlayPage>
    </>
  )

  async function handleOnSelectPayment(paymentMethod) {
    setIsRequestPayment(true)
    await onSelectPayment(paymentMethod)
    setTimeout(() => setIsRequestPayment(false), 300) // wait for animiation
  }

  function resetFormState() {
    refForm.current.hideError(refCardNumber.current)
    refForm.current.hideError(refCardHolderName.current)
    refForm.current.hideError(refExpireDate.current)
    refForm.current.hideError(refCVCInput.current)
  }

  function handleRememberCard(val) {
    if (isUseRemeberCreditCard && !val) {
      setIsOpenModalConfirmUnremember(true)
    } else {
      setIsWantToRememberCreditCard(val)
    }
  }

  function handleShowSpotErrorCreditCard(errorCode) {
    if (errorCode === 'invalid_security_code') {
      refForm.current.showError(refCVCInput.current, 'Security Code is invalid')
    }

    if (/number is invalid/.test(errorCode)) {
      refForm.current.showError(refCardNumber.current, 'Card Number is invalid')
    }

    if (/expiration date/.test(errorCode)) {
      refForm.current.showError(refExpireDate.current, 'Expire Date is invalid')
    }
  }

  function validationPhoneNumber(phoneNumber) {
    let _phoneNumber = phoneNumber.replace(/-/g, '')
    if (_phoneNumber === '') {
      return t('phoneNumberRequired')
    } else if (!/^(0[0-9]{8,9})$/.test(_phoneNumber)) {
      return t('phoneNumberInvalidPattern')
    } else {
      return ''
    }
  }

  async function handleOnSubmit() {
    if (stateView === '') {
      setWarningText(t('paymentMethodWarning'))
      setIsShowWarningText(true)
      return false
    }

    if (
      stateView === CustomerPaymentTypeEnum.BankTransfer ||
      stateView === CustomerPaymentTypeEnum.CashOnDelivery ||
      stateView === CustomerPaymentTypeEnum.SCBPromptPay ||
      stateView === CustomerPaymentTypeEnum.OmisePromptPay ||
      stateView === CustomerPaymentTypeEnum.OmiseTrueMoneyWallet ||
      stateView === CustomerPaymentTypeEnum.KBankThaiQR
    ) {
      handleOnSelectPayment({
        ...paymentMethod,
        type: stateView,
      })
      return false
    }

    if (stateView === CustomerPaymentTypeEnum.KPaymentKPlusNumber) {
      setIsKplusPhoneNumberSubmitted(true)
      const errorMsg = validationPhoneNumber(kplusPhoneNumber)
      if (errorMsg) {
        setKplusPhoneNumberErrorMsg(errorMsg)
        return false
      }

      handleOnSelectPayment({
        ...paymentMethod,
        type: stateView,
        phoneNumber: kplusPhoneNumber.replace(/-/g, ''),
      })
      return false
    }

    if (stateView === CustomerPaymentTypeEnum.KPaymentPromptPayNumber) {
      setIsKPromptpayPhoneNumberSubmitted(true)
      const errorMsg = validationPhoneNumber(kPromptpayPhoneNumber)
      if (errorMsg) {
        setKPromptpayPhoneNumberErrorMsg(errorMsg)
        return false
      }

      handleOnSelectPayment({
        ...paymentMethod,
        type: stateView,
        phoneNumber: kPromptpayPhoneNumber.replace(/-/g, ''),
      })
      return false
    }

    if (stateView === CustomerPaymentTypeEnum.KPaymentKPlusNumber) {
      setIsKplusPhoneNumberSubmitted(true)
      const errorMsg = validationPhoneNumber(kplusPhoneNumber)
      if (errorMsg) {
        setKplusPhoneNumberErrorMsg(errorMsg)
        return false
      }

      handleOnSelectPayment({
        ...paymentMethod,
        type: stateView,
        phoneNumber: kplusPhoneNumber.replace(/-/g, ''),
      })
      return false
    }

    if (!isEnableFeatureCreditCardManagement) {
      const errorMessage = getErrorMessage(refForm.current)

      if (!!errorMessage) {
        refForm.current.validateAll()
        return false
      }

      // Call get Omise Token if not have remember card
      if (isUseRemeberCreditCard && haveOmiseCreditCard) {
        // Remember Card ( Case 1 )
        // Send AccessToken and Amount to API.
        handleOnSelectPayment({
          ...paymentMethod,
          type: CustomerPaymentTypeEnum.OmiseCreditCard,
          rememberCard: isWantToRememberCreditCard,
        })
      } else {
        // Not Remember Card ( Case 2 )
        // Get Omise Token
        // Send Omise Token and AccessToken user from liff line and flag remember and Amount to API.
        // Success and Send Text Message and Close
        const { cardHolderName, cardNumber, expireDate, cvc } = formCreditData

        const expireDateSplit = expireDate.split('/')
        const expiration_month = expireDateSplit[0]
        const expiration_year = expireDateSplit[1]

        const card = isSavedCreditCard
          ? customerCreditCard
          : {
              name: cardHolderName,
              number: cardNumber,
              expiration_month: expiration_month,
              expiration_year: expiration_year,
              security_code: cvc,
            }

        setIsRequestPayment(true)
        omiseCreateToken({
          card,
          onSuccess: async ({ token }) => {
            await handleOnSelectPayment({
              creditCard: {
                cardBrand: creditCardBrand,
                cardLastDigits: card.number.slice(-4),
              },
              type: CustomerPaymentTypeEnum.OmiseCreditCard,
              token,
              rememberCard: isWantToRememberCreditCard,
            })

            // Keep credit card for recure token first customer
            setCustomerCreditCard(card)
          },
          onFailure: ({ errorMessage, errorMessageFromOmise, errorCode }) => {
            setIsRequestPayment(false)
            handleShowSpotErrorCreditCard(errorMessageFromOmise)
            if (errorMessage) {
              setErrorMsgCreditCard(errorMessage)
              !isDevelopment &&
                Sentry.captureException(new Error('Omise error: Payment Method'), {
                  error: errorMessage,
                })
            } else {
              setErrorMsgCreditCard(errorMessageFromOmise)
            }
          },
        })
      }
    } else {
      // CreditCard Management
      if (!selectedCreditCard) {
        setWarningText(t('paymentMethodWarning'))
        setIsShowWarningText(true)
      } else {
        handleOnSelectPayment({
          creditCard: {
            cardID: selectedCreditCard.cardID,
            cardBrand: selectedCreditCard.cardBrand,
            cardLastDigits: selectedCreditCard.cardLastDigits,
            cardholderName: selectedCreditCard.cardholderName,
          },
          type: CustomerPaymentTypeEnum.OmiseCreditCard,
        })
      }
    }
  }
}

export default PaymentMethod

const SummaryPriceBox = styled.div`
  margin-top: ${spacing.xlg}px;
  padding-bottom: ${spacing.xlg}px;

  button {
    margin: 0 auto;
  }

  .iphoneX & {
    padding-bottom: 35px;
  }
`

const CreditsCardViewBox = styled.div`
  padding-top: 30px;
  display: none;

  ${(props) =>
    props.show
      ? `
    display: block;
    `
      : ''}
`

const SwitchWrapper = styled.div`
  display: flex;
  margin-top: 9px;
`

const PaymentMethodWrapper = styled.div`
  margin-top: 14px;
  margin-bottom: 15px;
`

const ButtonWrapper = styled.div`
  display: flex;
`

const PreloadImage = styled.img`
  display: none;
`

const ModalContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  padding-top: 15px;
`

const ModalTitle = styled(Fonts.H2)`
  margin-bottom: ${spacing.md}px;
  text-align: center;
  color: black;
`

const ModalText = styled(Fonts.Base)`
  margin-bottom: ${spacing.md}px;
  text-align: center;
  color: black;
`

const ButtonCurrentLocationWrapper = styled.div`
  margin-bottom: ${spacing.md}px;
`

const FormPhoneNumber = styled.div`
  margin-top: 15px;
  margin-bottom: -10px;
  padding: 0 5px;
`

const LabelAddressWarningWrapper = styled.div`
  position: fixed;
  left: 0;
  right: 0;
  bottom: 140px;
  z-index: 100;
  transition: 0.3s;
  opacity: 0;
  transform: translate3d(0, 100%, 0);
  visibility: hidden;

  ${(props) =>
    props.show
      ? `
    opacity: 1;
    visibility: visible;
    transform: translate3d(0, 0, 0);
    `
      : ''}
`
